import type { ReactNode, FC } from 'react';
import React, { useCallback } from 'react';
import { cssMap } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import { LinkItem } from '@atlaskit/menu';

import { HOME } from '@confluence/named-routes/entry-points/routes';

import { useSelectedView } from '../useSelectedView';
import { View } from '../homeRouteParams';

import { useHomeNavigationAnalytics } from './useHomeNavigationAnalytics';

const styles = cssMap({
	root: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& [data-item-elem-before]': {
			width: '24px',
			marginRight: 'calc((12px - 12px * var(--homeNavIsCollapsed)) * var( --homeIsLegacy))',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& [data-item-title]': {
			// skeleton width must match: next/packages/home/src/Home/HomeNavigation/index.ts
			width: '190px',
			display: 'var(--homeNavCollapsedDisplay)',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > div': {
			columnGap: 'calc(12px - 12px * var(--homeNavIsCollapsed))',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > span': {
			gap: 'calc(12px - 12px * var(--homeNavIsCollapsed))',
		},
		backgroundColor: 'transparent',
		borderRadius: 'calc(3px - 3px * var(--homeIsLegacy))',
		padding: '8px calc(8px + 8px * var(--homeIsLegacy))',
		textDecoration: 'none',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
		color: `${token('color.text')} !important`,
		'&:visited': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
			color: `${token('color.text')} !important`,
			textDecoration: 'none',
		},
		'&:active': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
			color: `${token('color.text')} !important`,
			textDecoration: 'none',
		},
		'&:focus': {
			textDecoration: 'none',
		},
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
			textDecoration: 'none',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
			color: `${token('color.text')} !important`,
		},
		paddingBlock: '0px',
		minHeight: '36px',
	},
	isSelected: {
		backgroundColor: token('color.background.selected'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
		color: `${token('color.text.selected')} !important`,
		'&:visited': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
			color: `${token('color.text.selected')} !important`,
		},
		'&:active': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
			color: `${token('color.text.selected')} !important`,
		},
		'&:hover': {
			backgroundColor: token('color.background.selected.hovered'),
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
			color: `${token('color.text.selected')} !important`,
		},
		'&::before': {
			content: '""',
			position: 'absolute',
			top: '50%',
			left: 0,
			width: '4px',
			height: '16px',
			transform: 'translateY(-50%)',
			backgroundColor: token('color.border.selected'),
			borderRadius: `0 ${token('radius.small')} ${token('radius.small')} 0`,
		},
	},
});

type HrefItemProps = {
	href?: string;
	view?: never;
};

type ViewItemProps = {
	view: View;
	href?: never;
};

type HomeNavigationRenderProps = {
	isSelected: boolean;
};

type HomeNavigationItemProps = {
	icon: ReactNode;
	onClick?: () => void;
	render?: (props: HomeNavigationRenderProps) => ReactNode;
	children?: React.ReactNode;
} & (ViewItemProps | HrefItemProps);

const getHref = (view: View) => {
	if (view === View.OVERVIEW) {
		return HOME.toUrl();
	}
	return HOME.toUrl({ view });
};

export const HomeNavigationItem: FC<HomeNavigationItemProps> = ({
	view,
	href,
	icon,
	onClick,
	render,
	children,
}) => {
	const selectedView = useSelectedView();
	const fireAnalytics = useHomeNavigationAnalytics();

	const onLinkClick = useCallback(() => {
		view && fireAnalytics(view);
		onClick?.();
	}, [view, fireAnalytics, onClick]);

	return (
		<LinkItem
			onClick={onLinkClick}
			css={[styles.root, view && selectedView === view && styles.isSelected]}
			iconBefore={icon}
			isSelected={view && selectedView === view}
			href={href || (view && getHref(view))}
			target={href && '_blank'}
		>
			{render ? render({ isSelected: selectedView === view }) : children}
		</LinkItem>
	);
};
